import React, { useState } from "react";
import { Image as ImageIcon } from "lucide-react";

/**
 * ImageSlot — clearly-labeled placeholder for a real platform screenshot.
 *
 * Behaviour:
 *   • If `src` is passed explicitly, use it.
 *   • Otherwise auto-resolve to `/screenshots/<slotId>.png`.
 *   • If the file 404s (or fails to load for any reason), silently fall
 *     back to the labeled placeholder card. The build NEVER breaks on a
 *     missing screenshot.
 *
 * Canonical slot filename map (`/public/screenshots/<slotId>.png`):
 *   hero              — Command Center hero (Growth Score + $-Leaks + Approve-all-safe)
 *   growth-score      — Growth Score dial · 6 pillars
 *   dollar-leaks      — Top $-Leaks radar (5 rows with $/mo + confidence)
 *   pnl               — Contribution-Margin P&L Waterfall
 *   gauges            — ROI / Net-Margin gauges + KPI mini-tiles
 *   implement-feed    — 1-click Implement panel with Approve-ready ticker
 *   operator-modules  — Image / SEO / Inventory operator modules
 *   copilot           — AI Profit Copilot chat
 *
 * All slots are addressable by data-testid=`slot-<slotId>`.
 */
export const ImageSlot = ({
  slotId,
  label,
  src,
  alt,
  aspect = "16/10",
  accent = false,
  chrome = true,
}) => {
  const resolvedSrc = src || (slotId ? `/screenshots/${slotId}.png` : null);
  const [missing, setMissing] = useState(false);
  const testid = `slot-${slotId}`;

  const showImg = resolvedSrc && !missing;

  const inner = showImg ? (
    <img
      src={resolvedSrc}
      alt={alt || label}
      loading="lazy"
      onError={() => setMissing(true)}
      className="block w-full h-full object-cover"
    />
  ) : (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center"
      style={{
        background:
          "repeating-linear-gradient(45deg, rgba(15,23,42,0.02) 0 14px, rgba(15,23,42,0.05) 14px 28px)",
      }}
    >
      <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400">
        <ImageIcon className="w-5 h-5" />
      </div>
      <div className="text-[9px] sm:text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">
        Platform screenshot
      </div>
      <div className="text-slate-700 text-sm sm:text-base font-semibold leading-tight max-w-xs">
        {label}
      </div>
    </div>
  );

  return (
    <div
      data-testid={testid}
      data-slot-id={slotId}
      data-slot-state={showImg ? "image" : "placeholder"}
      className={`relative rounded-2xl border overflow-hidden shadow-lg bg-white ${
        accent
          ? "border-emerald-200 shadow-emerald-500/10 ring-1 ring-emerald-50"
          : "border-slate-200 shadow-slate-900/5"
      }`}
    >
      {chrome && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rose-300" />
            <span className="w-2 h-2 rounded-full bg-amber-300" />
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
          </div>
          <span className="text-[9px] font-mono text-slate-400 tracking-tight">
            platform.aigrowthoperator.xyz
          </span>
          <span className="w-10" />
        </div>
      )}
      <div style={{ aspectRatio: aspect }} className="w-full">
        {inner}
      </div>
    </div>
  );
};

export default ImageSlot;
